import {
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import dayjs from 'dayjs';
import {
  BbLotteryTicketEntry,
  BbLotteryTicketLadder,
  BbLotteryTicketTierGroup,
  bbGetLotteryTicketLadder,
  bbGetLotteryTickets,
} from '../../../services/blindBoxService';

/** 只读展示四组交易量赠券阶梯 */
const TICKET_TIER_GROUPS: { key: BbLotteryTicketTierGroup; titleKey: string }[] = [
  { key: 'volumeTiers', titleKey: 'module.blindBox.ticketDailyVol' },
  { key: 'predictVolumeTiers', titleKey: 'module.blindBox.ticketDailyPredictVol' },
  { key: 'leverageVolumeTiers', titleKey: 'module.blindBox.ticketDailyLeverageVol' },
  { key: 'tapGridVolumeTiers', titleKey: 'module.blindBox.ticketDailyTapGridVol' },
];

export function SectionLotteryTickets({ refreshKey }: { refreshKey: number }) {
  const t = useTranslate();
  const notify = useNotify();
  const [rows, setRows] = useState<BbLotteryTicketEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [userId, setUserId] = useState('');
  const [issueId, setIssueId] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [ladder, setLadder] = useState<BbLotteryTicketLadder | null>(null);

  const load = useCallback(() => {
    bbGetLotteryTicketLadder()
      .then(setLadder)
      .catch(() => setLadder(null));

    bbGetLotteryTickets({
      userId: userId || undefined,
      issueId: issueId ? parseInt(issueId, 10) || undefined : undefined,
      limit: rowsPerPage,
      offset: page * rowsPerPage,
    })
      .then((r) => {
        setRows(r.entries || []);
        setTotal(r.total || 0);
      })
      .catch((e: any) => notify(e?.message || 'Error', { type: 'error' }));
  }, [userId, issueId, rowsPerPage, page, notify]);

  useEffect(() => {
    setPage(0);
  }, [userId, issueId]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  return (
    <Stack spacing={2}>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('module.blindBox.ticketLadderTitle')}</TableCell>
              <TableCell>{t('module.blindBox.colTier')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>{t('module.blindBox.ticketPerBoxOpen')}</TableCell>
              <TableCell>{ladder?.boxOpenTickets ?? '—'}</TableCell>
            </TableRow>
            {TICKET_TIER_GROUPS.map((group) => {
              const tiers = ladder?.[group.key] ?? [];
              return (
                <TableRow key={group.key}>
                  <TableCell>{t(group.titleKey)}</TableCell>
                  <TableCell>
                    {tiers.length
                      ? tiers.map((x) => `${x.threshold} → ${x.ticketCount}`).join(', ')
                      : '—'}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
        <TextField size="small" label="userId" value={userId} onChange={(e) => setUserId(e.target.value)} sx={{ minWidth: 280 }} />
        <TextField size="small" label="issueId" value={issueId} onChange={(e) => setIssueId(e.target.value)} sx={{ minWidth: 120 }} />
        <Button onClick={load}>{t('module.blindBox.refresh')}</Button>
      </Stack>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {/*<TableCell>id</TableCell>*/}
              <TableCell>userId</TableCell>
              <TableCell>issueId</TableCell>
              <TableCell>issueNumber</TableCell>
              <TableCell>ticketNumber</TableCell>
              <TableCell>source</TableCell>
              <TableCell>createdAt</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id}>
                {/*<TableCell>{r.id}</TableCell>*/}
                <TableCell>{r.userId}</TableCell>
                <TableCell>{r.issueId}</TableCell>
                <TableCell>{r.issueNumber}</TableCell>
                <TableCell>{r.ticketNumber}</TableCell>
                <TableCell>{r.source}</TableCell>
                <TableCell>{r.createdAt ? dayjs(r.createdAt).format('YYYY-MM-DD HH:mm:ss') : '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_, nextPage) => setPage(nextPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          const next = parseInt(e.target.value, 10);
          setRowsPerPage(Number.isFinite(next) && next > 0 ? next : 50);
          setPage(0);
        }}
        rowsPerPageOptions={[20, 50, 100]}
      />
    </Stack>
  );
}

